import { FC, ReactElement, useState } from 'react';

import { IconButton } from '../../../../compositions';
import { Customer } from '../../../../entities/@account/Customer/Customer';
import { FormProps } from '../../../../entities/@forms/Form/Form';
import { useFormatDate, useTrans } from '../../../../hooks';
import { CustomerForm } from '../../../@forms/CustomerForm/CustomerForm';

import './MyData.scss';

interface MyDataProps extends FormProps<Customer> {
    customer: Customer;
    className?: string;
}

export const MyData: FC<MyDataProps> = ({
    customer,
    onSubmit,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const formatDate = useFormatDate();

    const [isEditMode, setIsEditMode] = useState<boolean>(false);

    const handleSetEditMode = (): void => {
        setIsEditMode(true);
    };

    const handleCancelEditMode = (): void => {
        setIsEditMode(false);
    };

    const handleSubmit = (formData: Customer): void => {
        onSubmit(formData);
        setIsEditMode(false);
    };

    return (
        <section className={`my-data ${className}`}>
            <header className="my-data__header">
                <h2 className="my-data__title">
                    {trans('containers.accountDashboardConfig.myData.title')}
                </h2>

                {!isEditMode && (
                    <IconButton
                        icon="edit"
                        text={trans('common.edit')}
                        onClick={handleSetEditMode}
                        className="my-data__edit-button"
                    />
                )}
            </header>

            {isEditMode && (
                <CustomerForm
                    customer={customer}
                    onSubmit={handleSubmit}
                    onCancel={handleCancelEditMode}
                    className="my-data__form"
                />
            )}

            {!isEditMode && (
                <div className="my-data__card">
                    <div className="my-data__value-wrapper">
                        <h3 className="my-data__label">
                            {trans('containers.accountDashboardConfig.myData.firstName')}
                        </h3>
                        <p className="my-data__value">
                            {customer.firstName}
                        </p>
                    </div>

                    <div className="my-data__value-wrapper">
                        <h3 className="my-data__label">
                            {trans('containers.accountDashboardConfig.myData.lastName')}
                        </h3>
                        <p className="my-data__value">
                            {customer.lastName}
                        </p>
                    </div>

                    <div className="my-data__value-wrapper">
                        <h3 className="my-data__label">
                            {trans('containers.accountDashboardConfig.myData.email')}
                        </h3>
                        <p className="my-data__value">
                            {customer.email}
                        </p>
                    </div>

                    <div className="my-data__value-wrapper">
                        <h3 className="my-data__label">
                            {trans('containers.accountDashboardConfig.myData.dateOfBirth')}
                        </h3>
                        <p className="my-data__value">
                            {formatDate(customer.birthday, { dateStyle: 'long' })}
                        </p>
                    </div>

                    <div className="my-data__value-wrapper">
                        <h3 className="my-data__label">
                            {trans('containers.accountDashboardConfig.myData.phoneNumber')}
                        </h3>
                        <p className="my-data__value">
                            {customer.phoneNumber}
                        </p>
                    </div>
                </div>
            )}
        </section>
    );
};
